import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Column } from '@tempmee/react';
import { useInviteProfessional } from '~/commonComponents/InviteProfessional/hooks';
import { TAB_INDEX } from '~/components/ProfessionalsHub/utils';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import { type ProfessionTypeEnum } from '~/models';
import { FavoritesEmpty, GlobalEmpty, NewToYouEmpty, WorkedWithEmpty } from './EmptyStates';
import {
  fromFavorite,
  fromRecent,
  fromRecommended,
  type IListItemData,
} from './professionalAdapters';
import { SkillsView } from './SkillsView';
import { TabContent } from './TabContent';
import { TabHeader, type TInviteTab, type ITabConfig } from './TabHeader';

const noop = () => {};

const tabIndexToValue = (index: number): TInviteTab => {
  if (index === TAB_INDEX.FAVORITES_LIST) return 'favorites';
  if (index === TAB_INDEX.RECENT_PROFESSIONALS) return 'worked_with';
  return 'new_to_you';
};

const tabValueToIndex = (value: TInviteTab): number => {
  if (value === 'favorites') return TAB_INDEX.FAVORITES_LIST;
  if (value === 'worked_with') return TAB_INDEX.RECENT_PROFESSIONALS;
  return TAB_INDEX.RECOMMENDED_PROS;
};

interface IEmbeddedInviteListProps {
  professionType: string;
  officeId: string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onAllEmptyChange?: (allEmpty: boolean) => void;
  initialTabHint?: TInviteTab;
}

export const EmbeddedInviteList = ({
  professionType,
  officeId,
  selectedIds,
  onSelectionChange,
  onAllEmptyChange,
  initialTabHint,
}: IEmbeddedInviteListProps) => {
  // Snapshot initial selection so the hook doesn't keep re-hydrating from prop changes
  const initialDefaultsRef = useRef<string[]>(selectedIds);
  const allProfessions = useAppSelector((state) => state.user.allProfessions);

  const getProfessionName = useCallback(
    (code: string) =>
      allProfessions?.find(({ code: c }: { code: string }) => c === code)?.name ?? code,
    [allProfessions],
  );

  const professionCode = useMemo(() => UserProfession.getCode(professionType), [professionType]);

  const {
    activeTab,
    selectedProfessionals,
    onSelectProfessionals,
    onTabChange,
    favoriteProfessionals,
    recentProfessionals,
    recommendedProfessionals,
    isLoading,
  } = useInviteProfessional({
    isOpen: true,
    isRefetchJob: false,
    defaultProfessionals: initialDefaultsRef.current,
    filterProfession: professionCode as ProfessionTypeEnum,
    officeId,
    onClosePopup: noop,
  });

  const favorites = useMemo(
    () => favoriteProfessionals.map((p: any) => fromFavorite(p, professionCode, getProfessionName)),
    [favoriteProfessionals, professionCode, getProfessionName],
  );
  const workedWith = useMemo(
    () => recentProfessionals.map((p: any) => fromRecent(p, professionCode, getProfessionName)),
    [recentProfessionals, professionCode, getProfessionName],
  );
  const newToYou = useMemo(
    () =>
      recommendedProfessionals.map((p: any) =>
        fromRecommended(p, professionCode, getProfessionName),
      ),
    [recommendedProfessionals, professionCode, getProfessionName],
  );

  // Mirror hook → form (single source of selection truth = hook's Set)
  const lastSyncedRef = useRef<string>('');
  useEffect(() => {
    const next = [...selectedProfessionals].sort();
    const key = next.join(',');
    if (key !== lastSyncedRef.current) {
      lastSyncedRef.current = key;
      onSelectionChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfessionals]);

  // Mirror form → hook when the parent removes an id externally (pill X
  // click). Key dep guards against react-hook-form's new array ref each
  // render; selectedProfessionals omitted from deps to avoid racing internal
  // row clicks before Direction 1 propagates.
  const selectedIdsKey = [...selectedIds].sort().join(',');
  useEffect(() => {
    const removedExternally = [...selectedProfessionals].filter((id) => !selectedIds.includes(id));
    if (removedExternally.length > 0) {
      onSelectProfessionals(removedExternally, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey]);

  const currentTab = tabIndexToValue(activeTab);

  const handleTabChange = useCallback(
    (next: TInviteTab) => {
      onTabChange(tabValueToIndex(next));
    },
    [onTabChange],
  );

  const tabsConfig: ITabConfig[] = useMemo(
    () => [
      {
        value: 'favorites',
        label: 'Favorites',
        count: favorites.length,
        testId: 'invite-tab-favorites',
      },
      {
        value: 'worked_with',
        label: 'Worked With',
        count: workedWith.length,
        testId: 'invite-tab-worked-with',
      },
      {
        value: 'new_to_you',
        label: 'New to You',
        count: newToYou.length,
        testId: 'invite-tab-new-to-you',
      },
    ],
    [favorites.length, workedWith.length, newToYou.length],
  );

  const currentList = useMemo(() => {
    if (currentTab === 'favorites') return favorites;
    if (currentTab === 'worked_with') return workedWith;
    return newToYou;
  }, [currentTab, favorites, workedWith, newToYou]);

  const currentEmptyState = useMemo(() => {
    if (currentTab === 'favorites') return <FavoritesEmpty />;
    if (currentTab === 'worked_with') return <WorkedWithEmpty />;
    return <NewToYouEmpty />;
  }, [currentTab]);

  const allEmpty = !isLoading && favorites.length + workedWith.length + newToYou.length === 0;

  const lastAllEmptyRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (lastAllEmptyRef.current !== allEmpty) {
      lastAllEmptyRef.current = allEmpty;
      onAllEmptyChange?.(allEmpty);
    }
  }, [allEmpty, onAllEmptyChange]);

  const hasPickedInitialTabRef = useRef(false);
  useEffect(() => {
    if (isLoading || hasPickedInitialTabRef.current) return;
    hasPickedInitialTabRef.current = true;

    if (initialTabHint && initialDefaultsRef.current.length > 0) {
      handleTabChange(initialTabHint);
      return;
    }

    if (favorites.length === 0) {
      if (workedWith.length > 0) {
        handleTabChange('worked_with');
      } else if (newToYou.length > 0) {
        handleTabChange('new_to_you');
      }
    }
  }, [
    isLoading,
    initialTabHint,
    favorites.length,
    workedWith.length,
    newToYou.length,
    handleTabChange,
  ]);

  const allSelectableIds = useMemo(() => currentList.map((p) => p.id), [currentList]);
  const allSelected =
    allSelectableIds.length > 0 && allSelectableIds.every((id) => selectedProfessionals.has(id));

  const handleToggleSelectAll = useCallback(() => {
    if (allSelectableIds.length === 0) return;
    if (allSelected) {
      onSelectProfessionals(allSelectableIds, false);
    } else {
      onSelectProfessionals(allSelectableIds, true);
    }
  }, [allSelected, allSelectableIds, onSelectProfessionals]);

  const [skillsPro, setSkillsPro] = useState<IListItemData | null>(null);
  const handleViewSkills = useCallback((pro: IListItemData) => setSkillsPro(pro), []);
  const handleBackFromSkills = useCallback(() => setSkillsPro(null), []);
  const lastSkillsProRef = useRef<IListItemData | null>(skillsPro);

  if (allEmpty) {
    return (
      <Column gap={4} data-testid="embedded-invite-list" data-cy="embedded-invite-list">
        <GlobalEmpty />
      </Column>
    );
  }

  if (skillsPro) {
    lastSkillsProRef.current = skillsPro;
  }
  const skillsToRender = skillsPro ?? lastSkillsProRef.current;
  const transition = { duration: 0.3, ease: 'easeInOut' as const };

  return (
    <Box
      data-testid="embedded-invite-list"
      data-cy="embedded-invite-list"
      style={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: 420,
      }}
    >
      <motion.div
        animate={{ x: skillsPro ? '-100%' : 0 }}
        transition={transition}
        style={{ width: '100%' }}
      >
        <Column gap={3}>
          {!isLoading && (
            <TabHeader
              activeTab={currentTab}
              onTabChange={handleTabChange}
              tabs={tabsConfig}
              allSelected={allSelected}
              onToggleSelectAll={handleToggleSelectAll}
              selectAllVisible={allSelectableIds.length > 0}
            />
          )}
          <TabContent
            isLoading={isLoading}
            professionals={currentList}
            selectedProfessionals={selectedProfessionals}
            onSelectProfessionals={onSelectProfessionals}
            onViewSkills={handleViewSkills}
            emptyComponent={currentEmptyState}
            testId={`invite-tab-content-${currentTab}`}
          />
        </Column>
      </motion.div>

      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: skillsPro ? 0 : '100%' }}
        transition={transition}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          backgroundColor: '#fff',
        }}
      >
        {skillsToRender && (
          <SkillsView
            fullName={skillsToRender.fullName}
            profession={skillsToRender.profession}
            professionCode={skillsToRender.professionCode || (professionCode as string)}
            education={skillsToRender.education}
            profileImage={skillsToRender.profileImage}
            onBack={handleBackFromSkills}
          />
        )}
      </motion.div>
    </Box>
  );
};
